import { appClient } from 'api/apiClient';
import { buildPageQuery } from 'api/v1Helpers';
import { isSystemBasket, normalizeBasket, toApiBasketStatus, toApiBasketType, type ApiBasketStatus } from 'types/baskets';
import { Basket, EventStatus } from 'types/common';

interface GetBasketsDataPayload {
    status: EventStatus;
    subType?: string | undefined | null;
    /** Parish UUID (Phase 0 selected parish). */
    parishId: string;
    headers?: { pageNumber?: number; pageSize?: number };
    name?: string;
}

async function listParishBaskets(params: {
    parishId: string;
    page: number;
    pageSize: number;
    status?: ApiBasketStatus;
    type?: string;
    search?: string;
    /**
     * Literal `'true'`/`'false'` — the backend validates this as an enum of those two strings,
     * not as a coerced boolean, so anything else is a 400. (Coercion was avoided deliberately
     * server-side: `z.coerce.boolean()` maps every non-empty string to true, which would make
     * `?isSystem=false` filter *to* the general fund.)
     */
    isSystem?: 'true' | 'false';
}) {
    const { parishId, page, pageSize, status, type, search, isSystem } = params;
    const qs = buildPageQuery(page, pageSize, {
        status,
        type,
        search,
        isSystem,
    });
    const response: any = await appClient.get(`parishes/${parishId}/baskets?${qs}`);
    const items = ((response?.items || []) as any[]).map(normalizeBasket);
    return {
        events: items,
        total: response?.total ?? items.length,
        page: response?.page ?? page,
        pageSize: response?.pageSize ?? pageSize,
    };
}

/**
 * List baskets for a parish (replaces POST /events/list/getEventList).
 * Returns `{ events }` so existing Redux fulfilled handlers stay intact.
 */
export const getBasketData = async (props: GetBasketsDataPayload) => {
    const { status, subType, parishId, headers, name } = props || {};
    if (!parishId) return null;

    const pageSize = headers?.pageSize ?? 20;
    const pageNumber = headers?.pageNumber ?? 0;
    const page = Math.max(1, pageNumber + 1);
    const search = (name || '').trim() || undefined;
    const type = toApiBasketType(subType);

    const statuses = (Array.isArray(status) ? status : status ? [status] : []).map((s) => toApiBasketStatus(String(s)));

    try {
        if (statuses.length > 1) {
            const pages = await Promise.all(
                statuses.map((s) =>
                    listParishBaskets({
                        parishId,
                        page,
                        pageSize,
                        status: s,
                        type,
                        search,
                    })
                )
            );
            const seen = new Set<string>();
            const events = pages
                .flatMap((p) => p.events)
                .filter((e) => {
                    const id = String(e.id);
                    if (seen.has(id)) return false;
                    seen.add(id);
                    return true;
                });
            const total = pages.reduce((sum, p) => sum + (p.total || 0), 0);
            const loadMore = pages.some((p) => p.page * p.pageSize < p.total);
            return { events, total, loadMore };
        }

        const single = statuses[0];
        return await listParishBaskets({
            parishId,
            page,
            pageSize,
            status: single,
            type,
            search,
        });
    } catch (error) {
        console.error('Error getting basket data:', error);
        throw error || new Error('An unknown error occurred.');
    }
};

/**
 * The parish's system-owned "General Fund" basket, or `null` if it has none.
 *
 * `null` is a real answer, not just an error case: a parish with no staff cannot be given a fund
 * (`Basket.createdById` is a required FK, and the backfill refuses to attribute the row to an
 * unrelated user), so callers must render an empty state rather than assume one exists. This is
 * permanent behaviour, not a backfill-window workaround.
 *
 * One filtered request — `?isSystem=true` returns exactly the fund. The result is still verified
 * client-side with `isSystemBasket`: if the param were ever dropped (an older deployment, a
 * schema that silently strips unknown params — which is exactly how the QR list behaved), the
 * response would be an unfiltered page and taking `[0]` would hand the parish QR whichever
 * basket sorted first. Trusting a filter we cannot see applied is the bug this whole area exists
 * to prevent.
 */
export const getParishGeneralFundBasket = async (parishId: string): Promise<Basket | null> => {
    if (!parishId) return null;
    const res = await listParishBaskets({
        parishId,
        page: 1,
        pageSize: 100,
        isSystem: 'true',
    });
    return res.events.find(isSystemBasket) ?? null;
};
